/**
 * Fullscreen Context
 *
 * Provides fullscreen mode functionality for tool pages.
 * When fullscreen, hides header and sidebar for maximum workspace.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from "react";
import PropTypes from "prop-types";

const FullscreenContext = createContext();

export const FullscreenProvider = ({ children }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const enterFullscreen = useCallback(() => {
    setIsFullscreen(true);
  }, []);

  const exitFullscreen = useCallback(() => {
    setIsFullscreen(false);
  }, []);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev);
  }, []);

  // Esc leaves full screen (28 Sep 2026). The map's full screen hides every control but its
  // own panel, and Lee could not find a way out; Esc is where anyone reaches first.
  useEffect(() => {
    if (!isFullscreen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setIsFullscreen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isFullscreen]);

  // Memoize so consumers get a stable reference (callbacks are already stable).
  const value = useMemo(
    () => ({
      isFullscreen,
      enterFullscreen,
      exitFullscreen,
      toggleFullscreen,
    }),
    [isFullscreen, enterFullscreen, exitFullscreen, toggleFullscreen]
  );

  return (
    <FullscreenContext.Provider value={value}>
      {children}
    </FullscreenContext.Provider>
  );
};

FullscreenProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useFullscreen = () => {
  const context = useContext(FullscreenContext);
  if (!context) {
    throw new Error("useFullscreen must be used within a FullscreenProvider");
  }
  return context;
};
